import { useEffect, useId, useRef, useState } from 'react';
import { api, type AgentPreview } from '../lib/api';
import { prettyAgentTitle } from '../lib/agentName';
import { Prism } from './Prism';

interface Props {
  open: boolean;
  slug: string | null;
  onClose: () => void;
  onSpawn: (slug: string) => void | Promise<void>;
}

/**
 * Agent detail / preview panel.
 *
 * Phase 7c. Surfaces the assembler's view of an agent before Mark commits
 * to a session: stage byte counts, Constitution sha, payload hash, and a
 * preview slice of the composed system prompt. Mark can spawn directly
 * from here.
 *
 * Carries the "review what you're calling on" pattern that V1 and V2 both
 * had as a first-class surface (V1 RoomView/AgentBriefing, V2 AgentDetail).
 * V1-cloud previously only exposed this as a JSON endpoint at
 * /api/agents/:slug/preview; this gives it a UI.
 */
export function AgentDetailSheet({ open, slug, onClose, onSpawn }: Props) {
  const [preview, setPreview] = useState<AgentPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const headingId = useId();
  const triggerRef = useRef<Element | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      const t = triggerRef.current as HTMLElement | null;
      if (t && typeof t.focus === 'function') t.focus();
      triggerRef.current = null;
      return;
    }
    triggerRef.current = document.activeElement;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Focus the Spawn CTA when the preview finishes loading; while loading,
  // fall back to the first focusable (the close button). Mark presses Enter
  // expecting "spawn the previewed agent," not "close the sheet."
  useEffect(() => {
    if (!open) return;
    if (preview && !loading) {
      const spawn = panelRef.current?.querySelector<HTMLElement>('button[data-spawn-cta]');
      if (spawn) {
        spawn.focus();
        return;
      }
    }
    const first = panelRef.current?.querySelector<HTMLElement>(
      'button, [href], input, [tabindex]:not([tabindex="-1"])',
    );
    first?.focus();
  }, [open, loading, preview]);

  useEffect(() => {
    if (!open || !slug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setExpanded(false);
    setPreview(null);
    api
      .agentPreview(slug)
      .then((res) => {
        if (cancelled) return;
        setPreview(res);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'failed to load agent preview');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, slug]);

  return (
    <div
      className={
        'simpliloquy-sheet-scrim fixed inset-0 z-50 transition-opacity ' +
        (open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none')
      }
      aria-hidden={!open}
    >
      <button
        type="button"
        aria-label="Close agent preview"
        tabIndex={-1}
        className="absolute inset-0 bg-night-900/60 backdrop-blur-[1px]"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className={
          'simpliloquy-sheet absolute left-0 right-0 bottom-0 bg-night-900 border-t border-night-600 ' +
          'rounded-t-2xl flex flex-col transition-transform ' +
          'md:left-1/2 md:right-auto md:-translate-x-1/2 md:bottom-8 md:w-[560px] md:rounded-2xl md:border md:shadow-2xl ' +
          (open ? 'translate-y-0' : 'translate-y-full md:translate-y-[110%]')
        }
        style={{
          height: '80dvh',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <div className="md:hidden flex items-center justify-center pt-2 pb-1">
          <div
            className="h-1 w-9 rounded-full"
            style={{ background: 'var(--agent-edge)' }}
          />
        </div>
        <div className="h-14 flex items-center px-4 border-b border-night-700 gap-3">
          {slug && <Prism slug={slug} size={20} active />}
          <h2 id={headingId} className="font-semibold text-sm text-paper-100 truncate">
            {slug ? prettyAgentTitle(slug) : 'Agent preview'}
          </h2>
          <div className="flex-1" />
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="simpliloquy-button-focus w-11 h-11 flex items-center justify-center rounded-md
                       hover:bg-night-800"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          {loading && <div className="text-paper-400 text-sm">Loading.</div>}
          {!loading && error && (
            <div className="text-paper-300 text-sm" role="alert">
              {error}
            </div>
          )}
          {!loading && preview && (
            <div className="flex flex-col gap-4">
              <DetailGrid preview={preview} />
              <Stages preview={preview} />
              <ComposedPreview preview={preview} expanded={expanded} onToggle={() => setExpanded((v) => !v)} />
            </div>
          )}
        </div>

        {slug && preview && (
          <div className="border-t border-night-700 p-3">
            <button
              type="button"
              data-spawn-cta="true"
              onClick={() => slug && onSpawn(slug)}
              className="simpliloquy-button-focus w-full h-12 rounded-md font-semibold text-[14px]
                         transition-[background-color,filter] hover:brightness-110"
              style={{
                background: 'var(--agent-accent)',
                color: 'var(--agent-fg-on-accent)',
              }}
            >
              Spawn {prettyAgentTitle(slug)}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailGrid({ preview }: { preview: AgentPreview }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <Field label="constitution sha" value={preview.constitution_sha256.slice(0, 12) + '…'} />
      <Field label="payload hash" value={preview.payload_hash.slice(0, 12) + '…'} />
      <Field label="assembled" value={prettyTime(preview.assembled_at)} />
      <Field label="composed" value={`${preview.composed_length.toLocaleString()} chars`} />
    </div>
  );
}

function Stages({ preview }: { preview: AgentPreview }) {
  const rows: Array<{ label: string; name: string; bytes: number }> = [
    { label: 'identity', name: preview.stages.identity.name, bytes: preview.stages.identity.length },
    ...preview.stages.canonicals.map((c) => ({ label: 'canonical', name: c.name, bytes: c.length })),
    { label: 'persona', name: preview.stages.persona.name, bytes: preview.stages.persona.length },
    { label: 'boot', name: preview.stages.boot.name, bytes: preview.stages.boot.length },
    ...preview.stages.skills.map((s) => ({ label: 'skill', name: s.name, bytes: s.length })),
  ];
  return (
    <div>
      <h3 className="font-mono text-[10px] uppercase tracking-mono-wide text-paper-400 mb-2">
        Assembled stages
      </h3>
      <div className="rounded-md border border-night-700 overflow-hidden">
        {rows.map((r, i) => (
          <div
            key={`${r.label}-${i}`}
            className="flex items-baseline gap-3 px-3 py-2 border-b border-night-800 last:border-b-0"
          >
            <span
              className="font-mono text-[10px] uppercase tracking-mono-wide w-20 flex-shrink-0"
              style={{ color: 'var(--agent-fg-soft)' }}
            >
              {r.label}
            </span>
            <span className="text-[13px] text-paper-100 flex-1 truncate">{r.name}</span>
            <span className="font-mono text-[11px] text-paper-400 flex-shrink-0">
              {r.bytes.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ComposedPreview({
  preview,
  expanded,
  onToggle,
}: {
  preview: AgentPreview;
  expanded: boolean;
  onToggle: () => void;
}) {
  const slice = expanded ? preview.composed_preview : preview.composed_preview.slice(0, 1200);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-mono text-[10px] uppercase tracking-mono-wide text-paper-400">
          Composed prompt preview
        </h3>
        <button
          type="button"
          onClick={onToggle}
          className="simpliloquy-button-focus font-mono text-[10px] uppercase tracking-mono-wide
                     text-paper-400 hover:text-paper-200 px-2 py-1 rounded"
        >
          {expanded ? 'collapse' : 'expand'}
        </button>
      </div>
      <pre
        className="font-mono text-[11px] leading-relaxed text-paper-300 bg-night-800
                   border border-night-700 rounded-md p-3 whitespace-pre-wrap overflow-x-auto"
      >
        {slice}
        {!expanded && preview.composed_preview.length > 1200 && '\n…'}
      </pre>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col leading-tight">
      <span
        className="font-mono text-[10px] uppercase tracking-mono-wide"
        style={{ color: 'var(--agent-fg-soft)' }}
      >
        {label}
      </span>
      <span className="font-mono text-[12px] text-paper-200 mt-0.5 truncate">{value}</span>
    </div>
  );
}

function prettyTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function CloseIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M3 3 L13 13 M13 3 L3 13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
